// time-sync.util.ts
import { FormControl, FormGroup } from "@angular/forms";
import { debounceTime, startWith, combineLatest, map } from "rxjs";
import { DestroyRef, inject, Signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

type Ctl<T> = FormControl<T>;

export interface MinutesTextPair {
  minutesKey: string; // ex: 'counterBeginMinutes'
  textKey: string; // ex: 'counterBeginText'
  // optionnel: démarrer la synchro minutes->text au init
  initFromMinutes?: boolean;
  // optionnel: custom parse/format (par défaut HH:mm)
  toText?: (mins: number | null | undefined) => string | null;
  toMinutes?: (txt: string | null | undefined) => number | null;
}

// minutes -> "H...:mm"  (heures sans zéro à gauche, minutes toujours 2 chiffres)
export function defaultMinutesToText(
  mins: number | null | undefined
): string | null {
  if (mins == null || Number.isNaN(mins)) return null;
  // on ne gère pas les négatifs ici, adapte si besoin
  const h = Math.trunc(mins / 60);
  const m = Math.abs(mins % 60);
  // heures sans padding (H illimité), minutes sur 2 chiffres
  return `${h}:${String(m).padStart(2, "0")}`;
}

// "H...:mm" -> minutes (heures illimitées)
export function defaultTextToMinutes(
  txt: string | null | undefined
): number | null {
  if (txt == null) return null;

  const s = txt.trim();
  // accepte 1+ chiffres pour les heures, et 00..59 pour les minutes
  const m = s.match(/^(\d+):([0-5]\d)$/);
  if (!m) return null;

  // NB: si les heures sont *énormes*, Number peut dépasser MAX_SAFE_INTEGER une fois converti en minutes.
  // Dans 99.9% des cas c'est ok, mais si tu as des valeurs extrêmes, pense à un BigInt ou une borne max.
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  return hours * 60 + minutes;
}

/**
 * Lie un ou plusieurs couples minutes<->texte.
 * - met à jour `...Text` quand `...Minutes` change (patch backend, reset, etc.)
 * - met à jour `...Minutes` quand l'utilisateur saisit `...Text`
 * - pose/retire une erreur `timeFormat` si le parse échoue
 */
export function setupMinutesTextSync(
  form: FormGroup,
  pairs: MinutesTextPair[],
  opts?: { destroyRef?: DestroyRef }
) {
  const destroyRef = opts?.destroyRef ?? inject(DestroyRef);

  for (const p of pairs) {
    const toText = p.toText ?? defaultMinutesToText;
    const toMinutes = p.toMinutes ?? defaultTextToMinutes;

    const minCtl = form.get(p.minutesKey) as Ctl<number>;
    const txtCtl = form.get(p.textKey) as Ctl<string>;

    if (!minCtl || !txtCtl) {
      // laisse silencieux ou throw selon préférence
      continue;
    }

    // init texte depuis minutes (optionnel)
    if (p.initFromMinutes !== false) {
      txtCtl.setValue(toText(minCtl.value), { emitEvent: false });
    }

    // minutes -> texte (patch/reset/backend)
    minCtl.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((mins) => {
        txtCtl.setValue(toText(mins), { emitEvent: false });
      });

    // texte -> minutes (saisie utilisateur)
    txtCtl.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((txt) => {
        const mins = toMinutes(txt);
        if (mins == null) {
          // merge non destructif des erreurs existantes
          txtCtl.setErrors({ ...(txtCtl.errors ?? {}), timeFormat: true });
        } else {
          const { timeFormat, ...rest } = (txtCtl.errors ?? {}) as any;
          txtCtl.setErrors(Object.keys(rest).length ? rest : null);
          minCtl.setValue(mins, { emitEvent: false }); // évite la boucle
          minCtl.markAsDirty();
          minCtl.updateValueAndValidity({ emitEvent: false });
        }
      });
  }
}

/**
 * combineLatest générique sur une liste de clés de form controls
 * Renvoie un Observable<void> qui émet à chaque changement (pratique pour .subscribe(cb))
 */
export function onFormKeysChange(
  form: FormGroup,
  keys: string[],
  debounceMs = 50,
  opts?: { destroyRef?: DestroyRef }
) {
  const streams = keys.map((k) => {
    const ctl = form.get(k)!;
    return ctl.valueChanges.pipe(startWith(ctl.value));
  });
  const obs = combineLatest(streams).pipe(
    debounceTime(debounceMs),
    map(() => void 0)
  );
  if (opts?.destroyRef) {
    return obs.pipe(takeUntilDestroyed(opts.destroyRef));
  }
  return obs;
}
